import {
  Box,
  Button,
  Container,
  Flex,
  GridItem,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import { faAward } from '@fortawesome/pro-regular-svg-icons'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'
import { getCoursePageSubheader } from '@/utils/seo'

import CourseQ from '@/queries/CourseQ'
import { COURSE_SLUG_SAT_PILOT, OTHER_DOMAIN } from '@/queries/constants'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import getTutorsSearchQuery from '@/client/services/search/getTutorsSearchQuery'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import CourseFollowButton from '@/components/Buttons/CourseFollowButton/CourseFollowButton'
import TextButtonWithChevron from '@/components/Buttons/TextButtonWithChevron'
import { ALL_TOPICS } from '@/components/CourseFinder'
import CourseNameHeader from '@/components/CourseNameHeader'
import KhanAcademyIcon from '@/components/Icons/KhanAcademyIcon'
import ShwGrid from '@/components/Layout/ShwGrid'
import ShwLink from '@/components/ShwLink'
import TutorPreview from '@/components/TutorPreview'

import { CourseSubheader } from './CourseSubheader'

const NUM_TUTORS_TO_SHOW = 5

export default function CourseHeader({
  queryCourseSlug,
  course,
}: {
  queryCourseSlug: string | null
  course?: Query | null
}): ReactElement {
  const query = useQuery()
  const user = useUser()

  const courseName = course == null ? 'All Courses' : course(CourseQ.name)
  const kaURL = course == null ? null : course(CourseQ.ka_url)

  const courseDomain = course == null ? null : course(CourseQ.domain)

  // Tutor Preview
  const tutorsWhere = getTutorsSearchQuery({
    userId: user.id,
    courseSlugs: course == null || queryCourseSlug == null ? [] : [queryCourseSlug],
    searchParam: '',
  })
  const tutors = query.many`${tutorsWhere} LIMIT ${NUM_TUTORS_TO_SHOW}`
  const tutorCount: number = query`COUNT(*) ${tutorsWhere}`
  const loggedOutSubheader = getCoursePageSubheader(queryCourseSlug ?? null)

  return (
    <Container>
      <ShwGrid>
        <GridItem colSpan={[12, 9]} maxWidth="100%">
          <HStack spacing="10px" mb="16px" align="center">
            <ShwLink variant="subtle" href="/explore">
              Explore
            </ShwLink>
            <FontAwesomeIcon icon={faChevronRight} size="sm" color={chakraTokenToVar('fg')} />
            <Text as="h1" fontWeight="bold">
              <CourseNameHeader
                courseName={courseName}
                courseSlug={queryCourseSlug}
                domain={courseDomain}
                hideSuperscript
              />
            </Text>
          </HStack>
          <Heading size="3xl" as="h1">
            <CourseNameHeader
              courseName={courseName}
              courseSlug={queryCourseSlug}
              domain={courseDomain}
            />
          </Heading>
          {user.isLoggedOut ? null : <CourseSubheader courseName={`${courseName}`} />}
        </GridItem>
        <GridItem colSpan={[12, 3]}>
          {(course != null && course(CourseQ.domain) === OTHER_DOMAIN) ||
          queryCourseSlug === COURSE_SLUG_SAT_PILOT ? null : (
            <Stack
              direction="column"
              spacing="16px"
              align={['flex-start', 'flex-end']}
              mt={['16px', '0px']}
            >
              <TutorPreview tutors={tutors} numShown={NUM_TUTORS_TO_SHOW} />
              <Flex justifyContent={['left', 'right']}>
                <TextButtonWithChevron
                  title={pluralize(tutorCount, course == null ? 'Tutor' : `${courseName} Tutor`)}
                  href={{
                    pathname: '/explore/tutors',
                    query: {
                      course: course == null ? ALL_TOPICS : queryCourseSlug,
                    },
                  }}
                />
              </Flex>
            </Stack>
          )}
        </GridItem>
        {user.isLoggedOut ? (
          <GridItem colSpan={12}>
            <CourseSubheader courseName={`${courseName}`} />
            <Box>{loggedOutSubheader()}</Box>
          </GridItem>
        ) : null}
        {user.isLoggedOut || queryCourseSlug === COURSE_SLUG_SAT_PILOT ? null : (
          <GridItem colSpan={12} display={['block', 'flex']} flexDir={['column', 'row']}>
            <Flex align="center" mt={6}>
              {course == null ? null : (
                <Flex mr="24px" align="center">
                  <CourseFollowButton course={course} />
                </Flex>
              )}
              <HStack spacing="24px" display={['none', 'none', 'flex']} align="center">
                {kaURL == null ? null : (
                  <ShwLink href={kaURL} variant="unstyled">
                    <Button
                      variant="outline"
                      leftIcon={
                        <KhanAcademyIcon
                          style={{
                            height: '16px',
                            width: 'auto',
                          }}
                        />
                      }
                    >
                      Practice on Khan Academy
                    </Button>
                  </ShwLink>
                )}
                {course == null || course(CourseQ.domain) === OTHER_DOMAIN ? null : (
                  <RequireLoginAndApproval>
                    <ShwLink
                      href={{
                        pathname: '/certification/get-certified',
                        query: { course: courseName },
                      }}
                    >
                      <Button
                        p={0}
                        variant="ghost"
                        leftIcon={
                          <FontAwesomeIcon icon={faAward} color={chakraTokenToVar('primary.fg')} />
                        }
                      >
                        Get Certified
                      </Button>
                    </ShwLink>
                  </RequireLoginAndApproval>
                )}
              </HStack>
            </Flex>
          </GridItem>
        )}
      </ShwGrid>
    </Container>
  )
}
